(function () {
  'use strict';

  if (customElements.get('retailer-locator-app')) {
    return;
  }

  var mapsLoaderPromise = null;
  var MARKER_CLUSTERER_SRC = 'https://unpkg.com/@googlemaps/markerclusterer/dist/index.min.js';

  function loadScript(src) {
    return new Promise(function (resolve, reject) {
      var script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.onload = resolve;
      script.onerror = function () {
        reject(new Error('Failed to load script: ' + src));
      };
      document.head.appendChild(script);
    });
  }

  // MarkerClusterer's UMD bundle subclasses google.maps.OverlayView at load
  // time, so it must be loaded strictly after the Maps JS API script, not in
  // parallel with it.
  function loadGoogleMaps(apiKey) {
    if (window.google && window.google.maps && window.google.maps.places && window.markerClusterer) {
      return Promise.resolve(window.google.maps);
    }

    if (mapsLoaderPromise) {
      return mapsLoaderPromise;
    }

    // loading=async: the script only bootstraps the API, and is ready when it calls the
    // callback (its onload can fire earlier). importLibrary() then loads each library and
    // fills in the google.maps.* namespace this file uses (Map, Marker, InfoWindow,
    // LatLngBounds, event, Point, Size, Geocoder, OverlayView, places).
    var CALLBACK = '__mmsRetailerMapsReady';
    var ready = new Promise(function (resolve) {
      window[CALLBACK] = function () {
        try { delete window[CALLBACK]; } catch (e) { window[CALLBACK] = undefined; }
        resolve();
      };
    });
    var mapsSrc = 'https://maps.googleapis.com/maps/api/js?key=' + encodeURIComponent(apiKey) + '&libraries=places&v=weekly&loading=async&callback=' + CALLBACK;

    mapsLoaderPromise = loadScript(mapsSrc)
      .then(function () { return ready; })
      .then(function () {
        if (!window.google || !window.google.maps || typeof window.google.maps.importLibrary !== 'function') {
          throw new Error('Google Maps failed to initialize');
        }
        return Promise.all(['core', 'maps', 'marker', 'geocoding', 'places'].map(function (name) {
          return window.google.maps.importLibrary(name);
        }));
      })
      .then(function () {
        if (!window.google.maps.Map || !window.google.maps.places) {
          throw new Error('Google Maps failed to initialize');
        }
        return loadScript(MARKER_CLUSTERER_SRC);
      })
      .then(function () {
        if (!window.markerClusterer) {
          throw new Error('MarkerClusterer failed to initialize');
        }
        return window.google.maps;
      });

    return mapsLoaderPromise;
  }

  var US_STATE_NAMES = {
    AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California',
    CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', DC: 'District of Columbia',
    FL: 'Florida', GA: 'Georgia', HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois',
    IN: 'Indiana', IA: 'Iowa', KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana',
    ME: 'Maine', MD: 'Maryland', MA: 'Massachusetts', MI: 'Michigan',
    MN: 'Minnesota', MS: 'Mississippi', MO: 'Missouri', MT: 'Montana',
    NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey',
    NM: 'New Mexico', NY: 'New York', NC: 'North Carolina', ND: 'North Dakota',
    OH: 'Ohio', OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania',
    RI: 'Rhode Island', SC: 'South Carolina', SD: 'South Dakota', TN: 'Tennessee',
    TX: 'Texas', UT: 'Utah', VT: 'Vermont', VA: 'Virginia', WA: 'Washington',
    WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
    // Territories
    AS: 'American Samoa', GU: 'Guam', MP: 'Northern Mariana Islands',
    PR: 'Puerto Rico', VI: 'U.S. Virgin Islands'
  };

  function fallbackAddressParts(retailer) {
    return [retailer.address1, retailer.city, retailer.state_code, retailer.postal_code].filter(Boolean);
  }

  // Swaps a 2-letter state code for its full name and, for US addresses only,
  // drops the trailing country name. Non-US addresses keep whatever country
  // text was already present (Google's formattedAddress includes it by default).
  function formatDisplayAddress(address, stateCode, countryCode) {
    if (!address) {
      return address;
    }

    var formatted = address;

    if (stateCode) {
      var stateName = US_STATE_NAMES[stateCode.toUpperCase()];
      if (stateName) {
        formatted = formatted.replace(new RegExp('(,\\s*)' + stateCode.toUpperCase() + '\\b'), '$1' + stateName);
      }
    }

    if (countryCode && countryCode.toUpperCase() === 'US') {
      formatted = formatted.replace(/,\s*(USA|United States(?: of America)?)\s*$/i, '');
    }

    return formatted.trim();
  }

  function stripUsaSuffix(address) {
    if (!address) {
      return address;
    }
    return address.replace(/,\s*(USA|United States(?: of America)?)\s*$/i, '').trim();
  }

  function replaceTokens(template, tokens) {
    return template.replace(/\{(\w+)\}/g, function (match, key) {
      return Object.prototype.hasOwnProperty.call(tokens, key) ? tokens[key] : match;
    });
  }

  var EARTH_RADIUS_MILES = 3958.8;

  function haversineMiles(lat1, lng1, lat2, lng2) {
    var toRad = function (deg) {
      return (deg * Math.PI) / 180;
    };
    var dLat = toRad(lat2 - lat1);
    var dLng = toRad(lng2 - lng1);
    var a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
    var c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return EARTH_RADIUS_MILES * c;
  }

  // destination_place_id is included only when we actually have one - the
  // geocoder-fallback path never does, so lat/lng alone is the destination.
  function buildDirectionsUrl(retailer) {
    var url = 'https://www.google.com/maps/dir/?api=1&destination=' + encodeURIComponent(retailer.lat + ',' + retailer.lng);
    if (retailer.place_id) {
      url += '&destination_place_id=' + encodeURIComponent(retailer.place_id);
    }
    return url;
  }

  // Custom brand marker icon for the own-store entry only - the one location
  // that should visually stand out as "us." Every other retailer uses
  // Google's plain default pin (no icon option set at all). Square source
  // image (mms-marker-icon-white-bg.png, 180x180), so it's centered like a
  // badge rather than anchored at a bottom point the way a teardrop pin
  // would be. 44px CSS size stays comfortably below the 180px source even at
  // 3x DPR (132 physical px), so this is always downscaling, never
  // upscaling/blurring - and reads larger than Google's default pin
  // (~25x41) so it clearly stands out as the flagship location at a glance.
  function buildOwnStoreIcon(url) {
    return {
      url: url,
      scaledSize: new google.maps.Size(44, 44),
      anchor: new google.maps.Point(22, 22)
    };
  }

  // Resolves a single retailer to live map data. Retailers with a place_id use the
  // current Place class (google.maps.places.Place) rather than the deprecated
  // PlacesService; retailers without one fall back to geocoding the raw address.
  //
  // The up-front field mask here is pure Essentials-tier data: formattedAddress
  // and location, the only things the map, the list card, and "Get directions"
  // need. displayName was dropped on purpose - the retailer's shown name is
  // authoritative from the feed (Shopify company_name via retailer.name), never
  // Google's business-profile name - and it was the only Pro-tier field in this
  // mask, so removing it drops the up-front resolve to the cheapest SKU.
  // nationalPhoneNumber and regularOpeningHours (Enterprise SKU) are likewise
  // NOT requested here; they're fetched on demand, one retailer at a time, when
  // the user opens the details modal (see loadRetailerDetails). fallbackPhone is
  // the retailer feed's own phone field - free public data, no Google call -
  // kept for the no-place_id path and as a last resort if the on-demand Places
  // lookup returns no phone.
  function resolveRetailer(retailer) {
    if (retailer.place_id) {
      var place = new google.maps.places.Place({
        id: retailer.place_id,
        requestedLanguage: 'en'
      });

      return place
        .fetchFields({
          fields: ['formattedAddress', 'location']
        })
        .then(function () {
          if (!place.location) {
            throw new Error('Place ' + retailer.place_id + ' has no location');
          }

          return {
            id: retailer.id,
            place_id: retailer.place_id,
            isOwnStore: !!retailer.isOwnStore,
            // Feed name only. 'Retailer' is a last-ditch guard against a
            // null/empty company_name so the tooltip, aria-label ("Show
            // Retailer on the map"), and modal title stay coherent rather
            // than blank.
            name: retailer.name || 'Retailer',
            address: formatDisplayAddress(
              place.formattedAddress || fallbackAddressParts(retailer).join(', '),
              retailer.state_code,
              retailer.country_code
            ),
            fallbackPhone: retailer.phone_number || '',
            lat: place.location.lat(),
            lng: place.location.lng()
          };
        });
    }

    var geocoder = new google.maps.Geocoder();
    var addressParts = fallbackAddressParts(retailer);
    var geocodeQuery = retailer.country_code ? addressParts.concat(retailer.country_code).join(', ') : addressParts.join(', ');
    var displayAddress = addressParts.join(', ');
    if (retailer.country_code && retailer.country_code.toUpperCase() !== 'US') {
      displayAddress += ', ' + retailer.country_code;
    }

    return geocoder.geocode({ address: geocodeQuery }).then(function (response) {
      var first = response && response.results && response.results[0];
      if (!first) {
        throw new Error('No geocode results for retailer ' + retailer.id);
      }

      return {
        id: retailer.id,
        place_id: null,
        isOwnStore: !!retailer.isOwnStore,
        // Same null/empty-name guard as the place_id branch above.
        name: retailer.name || 'Retailer',
        address: formatDisplayAddress(displayAddress, retailer.state_code, retailer.country_code),
        fallbackPhone: retailer.phone_number || '',
        lat: first.geometry.location.lat(),
        lng: first.geometry.location.lng()
      };
    });
  }

  customElements.define(
    'retailer-locator-app',
    class extends HTMLElement {
      connectedCallback() {
        this.endpoint = this.dataset.endpoint;
        this.apiKey = this.dataset.apiKey;
        this.loadingText = this.dataset.loadingText || 'Loading retailers...';
        this.emptyText = this.dataset.emptyText || 'No retailers are available right now.';
        this.errorText = this.dataset.errorText || 'Something went wrong loading the retailer map.';
        this.directionsText = this.dataset.directionsText || 'Get directions';
        this.searchSummaryText = this.dataset.searchSummaryText || '{count} found within {radius} mi of {location}.';
        this.searchEmptyText = this.dataset.searchEmptyText || 'No retailers found within {radius} mi. Try a larger radius.';
        this.viewportEmptyText =
          this.dataset.viewportEmptyText || 'No retailers are currently visible in this area of the map. Try panning or zooming out.';
        this.searchGeocodeErrorText =
          this.dataset.searchGeocodeErrorText || "Couldn't find that location — try a zip code or city, state.";
        this.searchGeolocationErrorText =
          this.dataset.searchGeolocationErrorText ||
          "We couldn't access your location. You can still search by zip code or city, state.";
        this.ownStorePlaceId = this.dataset.ownStorePlaceId || '';
        this.ownStoreBadgeText = this.dataset.ownStoreBadgeText || 'Our Store';
        this.ownStoreIconUrl = this.dataset.ownStoreIconUrl || '';

        // Details modal (deferred phone + hours lookup).
        this.detailsLoadingText = this.dataset.detailsLoadingText || 'Loading phone number and hours…';
        this.detailsErrorText =
          this.dataset.detailsErrorText || "Couldn't load the phone number and hours right now — please try again.";
        this.detailsPhoneUnavailableText = this.dataset.detailsPhoneUnavailableText || 'Phone number unavailable';
        this.detailsHoursUnavailableText = this.dataset.detailsHoursUnavailableText || 'Hours unavailable';

        this.statusEl = this.querySelector('[data-retailer-locator-status]');
        this.statusTextEl = this.querySelector('[data-retailer-locator-status-text]');
        this.layoutEl = this.querySelector('[data-retailer-locator-layout]');
        this.listEl = this.querySelector('[data-retailer-locator-list]');
        this.mapEl = this.querySelector('[data-retailer-locator-map]');

        this.searchInputEl = this.querySelector('[data-retailer-locator-search-input]');
        this.searchRadiusEl = this.querySelector('[data-retailer-locator-search-radius]');
        this.searchSubmitBtn = this.querySelector('[data-retailer-locator-search-submit]');
        this.searchGeolocateBtn = this.querySelector('[data-retailer-locator-search-geolocate]');
        this.searchClearBtn = this.querySelector('[data-retailer-locator-search-clear]');
        this.searchMessageEl = this.querySelector('[data-retailer-locator-search-message]');
        this.searchSummaryEl = this.querySelector('[data-retailer-locator-search-summary]');

        this.markers = [];
        this.markerClusterer = null;
        this.listItems = [];
        this.activeId = null;
        this.allRetailers = [];
        // Resolved { phone, hours } per retailer, keyed by retailer id. Populated
        // lazily by loadRetailerDetails on the first details-modal open for a
        // retailer and reused for every open after that - from EITHER the list
        // card or the map InfoWindow, since both pass through the same modal.
        // In-memory only: a plain object on the element instance, so it lives
        // exactly as long as this page load and is gone on reload. Never
        // sessionStorage/localStorage - phone number and hours may not be
        // persisted under Google's Places terms.
        this.detailsCache = {};
        // The active candidate set before the map-viewport filter is applied:
        // the full resolved list, or the current distance-search results if
        // one is active. Kept separate from allRetailers so an active search
        // survives panning (the viewport narrows within it), and separate
        // from whatever the list is currently showing (which the idle-driven
        // viewport filter narrows further, without touching this).
        this.candidateRetailers = [];
        this.searchOrigin = null;
        this.radiusMiles = parseInt((this.searchRadiusEl && this.searchRadiusEl.value) || '50', 10);

        this.init();
      }

      init() {
        var self = this;

        this.fetchRetailers()
          .then(function (retailers) {
            retailers = self.withOwnStore(retailers);

            if (!retailers.length) {
              self.showEmpty();
              return;
            }

            if (!self.apiKey) {
              throw new Error('Missing Google Maps API key theme setting');
            }

            return loadGoogleMaps(self.apiKey).then(function () {
              return self.resolveAll(retailers);
            });
          })
          .catch(function (error) {
            console.error('[retailer-locator]', error);
            self.showError();
          });
      }

      fetchRetailers() {
        return fetch(this.endpoint, { credentials: 'omit' })
          .then(function (response) {
            if (!response.ok) {
              throw new Error('Retailer list request failed with status ' + response.status);
            }
            return response.json();
          })
          .then(function (data) {
            return (data && data.retailers) || [];
          });
      }

      // Injects our own retail storefront as a synthetic retailer entry, before
      // the resolution step - it flows through the exact same Place.fetchFields
      // resolution/distance/sort/render pipeline as every stockist (isOwnStore
      // is just a display flag, not a special-cased data path). Fallback fields
      // below are the confirmed real address, only used if formattedAddress is
      // ever unavailable from the live Place lookup.
      withOwnStore(retailers) {
        if (!this.ownStorePlaceId) {
          return retailers;
        }

        var ownStoreEntry = {
          id: 'own-store',
          place_id: this.ownStorePlaceId,
          name: 'Mountain Madness Soap Co.',
          address1: '310 E Sherman Ave',
          city: "Coeur d'Alene",
          state_code: 'ID',
          postal_code: '83814',
          country_code: 'US',
          phone_number: null,
          isOwnStore: true
        };

        return [ownStoreEntry].concat(retailers);
      }

      resolveAll(retailers) {
        var self = this;

        return Promise.allSettled(retailers.map(resolveRetailer)).then(function (settled) {
          var resolved = [];

          settled.forEach(function (result, index) {
            if (result.status === 'fulfilled') {
              resolved.push(result.value);
            } else {
              console.warn('[retailer-locator] Failed to resolve retailer', retailers[index] && retailers[index].id, result.reason);
            }
          });

          if (!resolved.length) {
            self.showError();
            return;
          }

          self.render(resolved);
        });
      }

      showEmpty() {
        this.statusTextEl.textContent = this.emptyText;
        this.statusEl.classList.remove('retailer-locator__status--error');
        this.statusEl.hidden = false;
        this.layoutEl.hidden = true;
      }

      showError() {
        this.statusTextEl.textContent = this.errorText;
        this.statusEl.classList.add('retailer-locator__status--error');
        this.statusEl.hidden = false;
        this.layoutEl.hidden = true;
      }

      render(retailers) {
        this.statusEl.hidden = true;
        this.layoutEl.hidden = false;

        this.allRetailers = retailers;
        this.candidateRetailers = retailers;

        this.map = new google.maps.Map(this.mapEl, {
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          // disableDefaultUI was never set, so zoomControl already defaulted to
          // true - made explicit here so it's not just an implicit default.
          zoomControl: true
        });
        this.infoWindow = new google.maps.InfoWindow();

        // idle (not bounds_changed, which fires continuously mid-drag/zoom)
        // syncs the list to whatever's currently in view. Registered once
        // here since render() only runs once per page load; it only ever
        // touches the list (see updateListForViewport) - never markers, the
        // clusterer, or the map's own viewport - so it can't feedback-loop
        // with fitBounds/setCenter calls elsewhere or with itself.
        var self = this;
        this.map.addListener('idle', function () {
          self.updateListForViewport();
        });

        this.bindSearchControls();
        this.renderResults(retailers);
      }

      // Rebuilds markers + list for whatever subset is currently in view (the
      // full resolved list, or a search's filtered/sorted results). Shared so
      // a search doesn't need its own separate rendering path.
      renderResults(retailers) {
        var self = this;

        // Tear down the previous clusterer (if any) before rebuilding - clearMarkers()
        // removes its cluster overlays/markers from the map, setMap(null) detaches the
        // overlay itself. Without this, re-searching would leak the old cluster set and
        // double-render markers alongside the new one.
        if (this.markerClusterer) {
          this.markerClusterer.clearMarkers();
          this.markerClusterer.setMap(null);
          this.markerClusterer = null;
        }
        this.markers = [];
        this.listItems = [];
        this.listEl.innerHTML = '';

        if (!retailers.length) {
          this.renderResultsEmpty();
          return;
        }

        var bounds = new google.maps.LatLngBounds();
        var markerInstances = [];

        retailers.forEach(function (retailer) {
          var position = { lat: retailer.lat, lng: retailer.lng };
          // No `map` here - MarkerClusterer adds markers to the map itself,
          // grouping them into cluster pins where they're close together.
          var markerOptions = {
            position: position,
            title: retailer.name
          };
          // Everyone else gets no icon override at all, so google.maps.Marker
          // falls back to its own standard default pin.
          if (retailer.isOwnStore && self.ownStoreIconUrl) {
            markerOptions.icon = buildOwnStoreIcon(self.ownStoreIconUrl);
            // Google z-orders markers by vertical map position by default, not
            // creation order or an arbitrary fixed number - a plain-pin marker
            // further south can still cover a lower zIndex. MAX_ZINDEX is the
            // documented ceiling Google uses internally, guaranteeing this
            // marker renders above every default-positioned one regardless of
            // where it sits on the map.
            markerOptions.zIndex = google.maps.Marker.MAX_ZINDEX;
          }
          var marker = new google.maps.Marker(markerOptions);

          marker.addListener('click', function () {
            self.selectRetailer(retailer.id);
          });

          self.markers.push({ id: retailer.id, marker: marker, retailer: retailer });
          markerInstances.push(marker);
          bounds.extend(position);
        });

        this.markerClusterer = new markerClusterer.MarkerClusterer({
          map: this.map,
          markers: markerInstances
        });

        this.map.fitBounds(bounds);
        google.maps.event.addListenerOnce(this.map, 'bounds_changed', function () {
          if (self.map.getZoom() > 16) {
            self.map.setZoom(16);
          }
        });

        this.renderList(retailers);
      }

      // No markers to plot. If a search produced this (vs. a genuinely empty
      // retailer feed), center on the search origin at a zoom roughly matching
      // the selected radius so the searched area is still visible on the map.
      renderResultsEmpty() {
        if (this.searchOrigin) {
          this.map.setCenter({ lat: this.searchOrigin.lat, lng: this.searchOrigin.lng });
          this.map.setZoom(this.zoomForRadius(this.radiusMiles));
        }

        var message = document.createElement('div');
        message.className = 'retailer-locator__empty';
        message.textContent = this.searchOrigin
          ? replaceTokens(this.searchEmptyText, { radius: this.radiusMiles })
          : this.emptyText;
        this.listEl.appendChild(message);
      }

      zoomForRadius(radiusMiles) {
        if (radiusMiles <= 25) {
          return 9;
        }
        if (radiusMiles <= 50) {
          return 8;
        }
        return 7;
      }

      // Fires on every map idle (pan/zoom settle). Narrows candidateRetailers
      // (the full list, or the active distance search's results) down to
      // whatever's in the current viewport and re-renders the list only -
      // markers, the clusterer, and the map's own viewport are never touched
      // here, so this can't fight with fitBounds/search/clear or loop back
      // into another idle event. Own-store gets no special treatment: it's
      // just another entry in candidateRetailers, so it only shows when it's
      // actually in view, same as every other retailer.
      updateListForViewport() {
        if (!this.candidateRetailers.length) {
          // Nothing to show regardless of viewport - renderResultsEmpty()
          // already owns the list for this case (genuinely empty feed, or a
          // distance search with zero results), don't overwrite its message.
          return;
        }

        var visible = this.applyViewportFilter(this.candidateRetailers);

        this.listItems = [];
        this.listEl.innerHTML = '';

        if (!visible.length) {
          this.renderViewportEmpty();
          return;
        }

        this.renderList(visible);

        // Rebuilding the list loses any is-active highlight from a prior
        // marker/list click - reapply it if that retailer is still visible.
        if (this.activeId) {
          this.listItems.forEach(function (item) {
            item.el.classList.toggle('is-active', item.id === this.activeId);
          }, this);
        }
      }

      applyViewportFilter(retailers) {
        if (!this.map) {
          return retailers;
        }

        var bounds = this.map.getBounds();
        if (!bounds) {
          return retailers;
        }

        return retailers.filter(function (retailer) {
          return bounds.contains({ lat: retailer.lat, lng: retailer.lng });
        });
      }

      // Distinct from renderResultsEmpty()'s messages (genuinely-empty feed,
      // or zero distance-search results): this is "there are retailers, just
      // not in view right now," so it must not read like the search failed.
      // Never touches the map (no setCenter/setZoom) - this runs from the
      // idle handler, and moving the map here would fight the user's own pan.
      renderViewportEmpty() {
        var message = document.createElement('div');
        message.className = 'retailer-locator__empty';
        message.textContent = this.viewportEmptyText;
        this.listEl.appendChild(message);
      }

      bindSearchControls() {
        var self = this;

        if (!this.searchSubmitBtn) {
          return;
        }

        this.searchSubmitBtn.addEventListener('click', function () {
          self.runTextSearch();
        });

        this.searchInputEl.addEventListener('keydown', function (e) {
          if (e.key === 'Enter') {
            e.preventDefault();
            self.runTextSearch();
          }
        });

        this.searchGeolocateBtn.addEventListener('click', function () {
          self.runGeolocationSearch();
        });

        this.searchRadiusEl.addEventListener('change', function () {
          self.radiusMiles = parseInt(self.searchRadiusEl.value, 10) || 50;
          if (self.searchOrigin) {
            self.applyFilter();
          }
        });

        this.searchClearBtn.addEventListener('click', function () {
          self.resetSearch();
        });
      }

      runTextSearch() {
        var query = this.searchInputEl.value.trim();
        if (!query) {
          return;
        }

        this.hideSearchMessage();
        var geocoder = new google.maps.Geocoder();
        var self = this;

        geocoder
          .geocode({ address: query })
          .then(function (response) {
            var first = response && response.results && response.results[0];
            if (!first) {
              throw new Error('No geocode results for search query: ' + query);
            }

            self.applySearch({
              lat: first.geometry.location.lat(),
              lng: first.geometry.location.lng(),
              label: stripUsaSuffix(first.formatted_address) || query
            });
          })
          .catch(function (error) {
            console.warn('[retailer-locator] Search geocode failed', error);
            self.showSearchMessage(self.searchGeocodeErrorText);
          });
      }

      runGeolocationSearch() {
        this.hideSearchMessage();

        if (!('geolocation' in navigator)) {
          this.showSearchMessage(this.searchGeolocationErrorText);
          return;
        }

        var self = this;
        navigator.geolocation.getCurrentPosition(
          function (position) {
            self.applySearch({
              lat: position.coords.latitude,
              lng: position.coords.longitude,
              label: 'your location'
            });
          },
          function (error) {
            console.warn('[retailer-locator] Geolocation failed', error);
            self.showSearchMessage(self.searchGeolocationErrorText);
          },
          { timeout: 10000 }
        );
      }

      applySearch(origin) {
        this.searchOrigin = origin;
        this.radiusMiles = parseInt(this.searchRadiusEl.value, 10) || 50;
        this.searchClearBtn.hidden = false;
        this.applyFilter();
      }

      applyFilter() {
        if (!this.searchOrigin) {
          return;
        }

        var self = this;
        var withDistance = this.allRetailers.map(function (retailer) {
          return Object.assign({}, retailer, {
            distanceMiles: haversineMiles(self.searchOrigin.lat, self.searchOrigin.lng, retailer.lat, retailer.lng)
          });
        });

        var within = withDistance
          .filter(function (retailer) {
            return retailer.distanceMiles <= self.radiusMiles;
          })
          .sort(function (a, b) {
            return a.distanceMiles - b.distanceMiles;
          });

        this.showSearchSummary(within.length);
        this.candidateRetailers = within;
        this.renderResults(within);
      }

      showSearchSummary(count) {
        var countLabel = count === 1 ? '1 retailer' : count + ' retailers';
        this.searchSummaryEl.textContent = replaceTokens(this.searchSummaryText, {
          count: countLabel,
          radius: this.radiusMiles,
          location: this.searchOrigin ? this.searchOrigin.label : ''
        });
        this.searchSummaryEl.hidden = false;
      }

      showSearchMessage(text) {
        this.searchMessageEl.textContent = text;
        this.searchMessageEl.hidden = false;
      }

      hideSearchMessage() {
        this.searchMessageEl.hidden = true;
        this.searchMessageEl.textContent = '';
      }

      resetSearch() {
        this.searchOrigin = null;
        this.searchInputEl.value = '';
        this.searchClearBtn.hidden = true;
        this.searchSummaryEl.hidden = true;
        this.hideSearchMessage();
        this.candidateRetailers = this.allRetailers;
        this.renderResults(this.allRetailers);
      }

      renderList(retailers) {
        var self = this;
        var fragment = document.createDocumentFragment();

        retailers.forEach(function (retailer) {
          // A plain div (not a <button>) because it needs to contain the real
          // "View phone number and hours" <button> below — a button can't
          // validly nest another.
          var item = document.createElement('div');
          item.className = 'retailer-locator__item';
          item.dataset.retailerId = retailer.id;
          item.setAttribute('role', 'button');
          item.setAttribute('tabindex', '0');
          item.setAttribute('aria-label', 'Show ' + retailer.name + ' on the map');

          if (typeof retailer.distanceMiles === 'number') {
            var distance = document.createElement('p');
            distance.className = 'retailer-locator__item-distance';
            distance.textContent = retailer.distanceMiles.toFixed(1) + ' mi';
            item.appendChild(distance);
          }

          // Badge nests inside the name paragraph (not a preceding sibling) so it
          // flows inline with the store name instead of forcing its own line -
          // distance above stays exactly where it sits on every other card.
          var name = document.createElement('p');
          name.className = 'retailer-locator__item-name';
          name.appendChild(document.createTextNode(retailer.name));
          if (retailer.isOwnStore) {
            name.appendChild(self.buildOwnStoreBadge());
          }
          item.appendChild(name);

          if (retailer.address) {
            var address = document.createElement('p');
            address.className = 'retailer-locator__item-address';
            address.textContent = retailer.address;
            item.appendChild(address);
          }

          // Phone number and the condensed "Today: hours" line are gone from
          // the default card - that data is no longer resolved up front. The
          // link below is the only entry point to it now.
          item.appendChild(self.buildDetailsLink(retailer));

          item.appendChild(self.buildDirectionsLink(retailer));

          item.addEventListener('click', function () {
            self.selectRetailer(retailer.id, true);
          });
          item.addEventListener('keydown', function (e) {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              self.selectRetailer(retailer.id, true);
            }
          });

          self.listItems.push({ id: retailer.id, el: item });
          fragment.appendChild(item);
        });

        this.listEl.appendChild(fragment);
      }

      selectRetailer(id, fromListClick) {
        var entry = this.markers.filter(function (m) {
          return m.id === id;
        })[0];

        if (!entry) {
          return;
        }

        this.activeId = id;

        this.listItems.forEach(function (item) {
          item.el.classList.toggle('is-active', item.id === id);
        });

        var activeItem = this.listItems.filter(function (item) {
          return item.id === id;
        })[0];
        if (activeItem && !fromListClick) {
          activeItem.el.scrollIntoView({ block: 'nearest' });
        }

        this.map.panTo(entry.marker.getPosition());
        if (this.map.getZoom() < 14) {
          this.map.setZoom(14);
        }

        this.infoWindow.setContent(this.buildInfoWindowContent(entry.retailer));
        this.infoWindow.open({ map: this.map, anchor: entry.marker });
      }

      buildInfoWindowContent(retailer) {
        var wrapper = document.createElement('div');
        wrapper.className = 'retailer-locator__info-window';

        // Badge nests inside the header (not a preceding sibling), matching the
        // list card - see renderList().
        var strong = document.createElement('strong');
        strong.appendChild(document.createTextNode(retailer.name));
        if (retailer.isOwnStore) {
          strong.appendChild(this.buildOwnStoreBadge());
        }
        wrapper.appendChild(strong);

        if (retailer.address) {
          var address = document.createElement('div');
          address.textContent = retailer.address;
          wrapper.appendChild(address);
        }

        // Same as the list card: no phone line, no "Today: hours" line up
        // front. buildDetailsLink is the shared entry point into the same
        // deferred lookup + modal.
        wrapper.appendChild(this.buildDetailsLink(retailer));

        wrapper.appendChild(this.buildDirectionsLink(retailer));

        return wrapper;
      }

      buildOwnStoreBadge() {
        var badge = document.createElement('span');
        badge.className = 'retailer-locator__own-store-badge';
        badge.textContent = this.ownStoreBadgeText;
        return badge;
      }

      buildDirectionsLink(retailer) {
        var link = document.createElement('a');
        link.className = 'retailer-locator__directions-link';
        link.href = buildDirectionsUrl(retailer);
        link.target = '_blank';
        link.rel = 'noopener';
        link.textContent = this.directionsText;
        link.addEventListener('click', function (e) {
          e.stopPropagation();
        });
        return link;
      }

      // Shared between the sidebar list cards and the map InfoWindow popups -
      // one template, two mount points, so a single change here covers both
      // surfaces by construction. Always rendered now (for every retailer,
      // place_id or not): phone number and hours aren't resolved up front any
      // more, so this link is the sole entry point to them, and the actual
      // lookup happens on click in openHoursModal.
      buildDetailsLink(retailer) {
        var self = this;
        var wrap = document.createElement('div');
        wrap.className = 'retailer-locator__hours';

        var link = document.createElement('button');
        link.type = 'button';
        link.className = 'retailer-locator__hours-link';
        link.textContent = 'View phone number and hours';
        link.addEventListener('click', function (e) {
          e.stopPropagation();
          self.openHoursModal(retailer);
        });
        wrap.appendChild(link);

        return wrap;
      }

      openHoursModal(retailer) {
        this.closeHoursModal();
        var self = this;

        var overlay = document.createElement('div');
        overlay.className = 'retailer-locator__modal-overlay';
        overlay.setAttribute('role', 'dialog');
        overlay.setAttribute('aria-modal', 'true');
        overlay.setAttribute('aria-label', retailer.name + ' phone number and hours');
        overlay.addEventListener('click', function (e) {
          if (e.target === overlay) {
            self.closeHoursModal();
          }
        });

        var onKeyDown = function (e) {
          if (e.key === 'Escape') {
            self.closeHoursModal();
          }
        };
        document.addEventListener('keydown', onKeyDown);
        overlay._cleanup = function () {
          document.removeEventListener('keydown', onKeyDown);
        };

        var modal = document.createElement('div');
        modal.className = 'retailer-locator__modal';

        var closeBtn = document.createElement('button');
        closeBtn.type = 'button';
        closeBtn.className = 'retailer-locator__modal-close';
        closeBtn.setAttribute('aria-label', 'Close');
        closeBtn.innerHTML = '&times;';
        closeBtn.addEventListener('click', function () {
          self.closeHoursModal();
        });
        modal.appendChild(closeBtn);

        var title = document.createElement('h2');
        title.className = 'retailer-locator__modal-title';
        title.textContent = retailer.name;
        modal.appendChild(title);

        // Stable container the (possibly async) details render targets. Every
        // state - loading, resolved, error - swaps this element's contents.
        var body = document.createElement('div');
        body.className = 'retailer-locator__modal-body';
        modal.appendChild(body);

        overlay.appendChild(modal);
        document.body.appendChild(overlay);
        this.activeHoursOverlay = overlay;
        closeBtn.focus();

        var cached = this.detailsCache[retailer.id];
        if (cached) {
          // Same retailer already resolved this page load (from either
          // surface) - reuse it, zero API calls.
          this.renderModalDetails(body, cached);
          return;
        }

        this.renderModalLoading(body);
        this.loadRetailerDetails(retailer)
          .then(function (details) {
            // Modal may have been closed or replaced by another retailer's
            // while this was in flight - only paint if it's still the one.
            if (self.activeHoursOverlay !== overlay) {
              return;
            }
            self.renderModalDetails(body, details);
          })
          .catch(function (error) {
            console.warn('[retailer-locator] Details lookup failed', retailer.id, error);
            if (self.activeHoursOverlay !== overlay) {
              return;
            }
            self.renderModalError(body);
          });
      }

      // Resolves { phone, hours } for one retailer, on demand. Order:
      //   1. in-memory cache hit  -> no work, no call
      //   2. no place_id          -> phone from the feed's fallback field, no
      //                              hours data exists for these at all, and
      //                              crucially no Google call
      //   3. has place_id         -> exactly one Place.fetchFields scoped to
      //                              just the Enterprise-tier phone + hours
      // A successful resolve is cached for the page-load lifetime; a failure is
      // NOT cached, so reopening the modal retries.
      loadRetailerDetails(retailer) {
        var self = this;

        if (this.detailsCache[retailer.id]) {
          return Promise.resolve(this.detailsCache[retailer.id]);
        }

        if (!retailer.place_id) {
          var fallback = {
            phone: retailer.fallbackPhone || '',
            hours: null,
            hoursUnavailable: true
          };
          this.detailsCache[retailer.id] = fallback;
          return Promise.resolve(fallback);
        }

        var place = new google.maps.places.Place({
          id: retailer.place_id,
          requestedLanguage: 'en'
        });

        return place
          .fetchFields({
            fields: ['nationalPhoneNumber', 'regularOpeningHours']
          })
          .then(function () {
            var openingHours = place.regularOpeningHours || null;
            var details = {
              phone: place.nationalPhoneNumber || retailer.fallbackPhone || '',
              hours: openingHours && openingHours.weekdayDescriptions && openingHours.weekdayDescriptions.length
                ? openingHours.weekdayDescriptions
                : null,
              hoursUnavailable: !(openingHours && openingHours.weekdayDescriptions && openingHours.weekdayDescriptions.length)
            };
            self.detailsCache[retailer.id] = details;
            return details;
          });
      }

      renderModalLoading(body) {
        body.innerHTML = '';
        var loading = document.createElement('div');
        loading.className = 'retailer-locator__modal-loading';
        loading.textContent = this.detailsLoadingText;
        body.appendChild(loading);
      }

      // Friendly inline message, same intent as the distance-search geocode
      // failure text - never leave the modal stuck on the loading state.
      renderModalError(body) {
        body.innerHTML = '';
        var error = document.createElement('div');
        error.className = 'retailer-locator__modal-error';
        error.textContent = this.detailsErrorText;
        body.appendChild(error);
      }

      renderModalDetails(body, details) {
        body.innerHTML = '';

        var phone = document.createElement('div');
        phone.className = 'retailer-locator__modal-phone';
        if (details.phone) {
          var tel = document.createElement('a');
          tel.href = 'tel:' + details.phone.replace(/[^0-9+]/g, '');
          tel.textContent = details.phone;
          phone.appendChild(tel);
        } else {
          phone.textContent = this.detailsPhoneUnavailableText;
        }
        body.appendChild(phone);

        if (details.hours && details.hours.length) {
          var list = document.createElement('ul');
          list.className = 'retailer-locator__modal-hours-list';
          details.hours.forEach(function (line) {
            var li = document.createElement('li');
            li.textContent = line;
            list.appendChild(li);
          });
          body.appendChild(list);
        } else {
          var noHours = document.createElement('div');
          noHours.className = 'retailer-locator__modal-hours-unavailable';
          noHours.textContent = this.detailsHoursUnavailableText;
          body.appendChild(noHours);
        }
      }

      closeHoursModal() {
        if (this.activeHoursOverlay) {
          if (this.activeHoursOverlay._cleanup) {
            this.activeHoursOverlay._cleanup();
          }
          this.activeHoursOverlay.remove();
          this.activeHoursOverlay = null;
        }
      }
    }
  );
})();
